import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { hoursToMinutes } from '../utils/duration';
import './ScheduleEntryModal.css';

/**
 * Schedule-entry modal (sales rework Phase 10, spec §6): raised when a stage
 * move answers 400 SCHEDULE_ENTRY_REQUIRED — entering the stage needs a
 * scheduled task (who does the work and when). Submits the SAME stage change
 * again with the schedule_entry attached; the server creates the calendar
 * task, notifies the assignee(s) and timelines it.
 *
 * demand: { requires_schedule_entry, form_template_name } (the 400's details)
 */
function ScheduleEntryModal({ stageName, lineLabel, demand, onCancel, onSubmit, submitting, submitError }) {
  const { authFetch } = useAuth();
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [assignType, setAssignType] = useState('user');
  const [assignedToId, setAssignedToId] = useState('');
  const [assignedGroup, setAssignedGroup] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [startTime, setStartTime] = useState('');
  // Entered in hours (Gareth 2026-09-21); the API still takes minutes.
  const [durationHours, setDurationHours] = useState('1');
  const [notes, setNotes] = useState('');
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      // NOT /admin/users — that router is admin/manager-gated and the reps
      // driving stage moves are commercial. This endpoint serves all roles.
      authFetch('/api/v1/opportunities/schedulable-users').then(r => r.json()).catch(() => ({ data: [] })),
      authFetch('/api/v1/groups?purpose=operational').then(r => r.json()).catch(() => ({ data: [] }))
    ]).then(([usersData, groupsData]) => {
      if (cancelled) return;
      setUsers(usersData.data || []);
      setGroups(groupsData.data || []);
    });
    return () => { cancelled = true; };
  }, [authFetch]);

  const handleSubmit = () => {
    setLocalError('');
    if (!scheduledDate) {
      setLocalError('A target date is required');
      return;
    }
    if (assignType === 'user' && !assignedToId) {
      setLocalError('Choose who will do the work');
      return;
    }
    if (assignType === 'group' && !assignedGroup) {
      setLocalError('Choose a group');
      return;
    }
    onSubmit({
      scheduled_date: scheduledDate,
      scheduled_start_time: startTime || null,
      estimated_duration_mins: hoursToMinutes(durationHours) || 60,
      assigned_to_id: assignType === 'user' ? assignedToId : null,
      assigned_group: assignType === 'group' ? assignedGroup : null,
      notes: notes.trim() || null
    });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content schedule-entry-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Schedule: {stageName}</h2>
          <button type="button" className="modal-close" onClick={onCancel} disabled={submitting}>&times;</button>
        </div>
        <div className="modal-body">
          <p className="schedule-entry-intro">
            {lineLabel && <strong>{lineLabel} — </strong>}
            entering <strong>"{stageName}"</strong> needs a scheduled task
            {demand?.form_template_name
              ? <> — the visit completes the <strong>{demand.form_template_name}</strong> form at sign-off.</>
              : '.'}
          </p>

          {(localError || submitError) && <div className="modal-error">{localError || submitError}</div>}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="se-date">Target date *</label>
              <input id="se-date" type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label htmlFor="se-time">Start time</label>
              <input id="se-time" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </div>
            <div className="form-group">
              <label htmlFor="se-duration">Duration (hours)</label>
              <input id="se-duration" type="number" min="0.25" step="0.25" value={durationHours} onChange={(e) => setDurationHours(e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label>Assign to *</label>
            <div className="assign-type-toggle">
              <label className="radio-option">
                <input type="radio" checked={assignType === 'user'} onChange={() => setAssignType('user')} />
                A person
              </label>
              <label className="radio-option">
                <input type="radio" checked={assignType === 'group'} onChange={() => setAssignType('group')} />
                A group
              </label>
            </div>
            {assignType === 'user' ? (
              <select value={assignedToId} onChange={(e) => setAssignedToId(e.target.value)}>
                <option value="">Select a team member...</option>
                {users.map(user => (
                  <option key={user.user_id} value={user.user_id}>
                    {user.full_name}{user.role ? ` (${user.role})` : ''}
                  </option>
                ))}
              </select>
            ) : (
              <select value={assignedGroup} onChange={(e) => setAssignedGroup(e.target.value)}>
                <option value="">Select a group...</option>
                {groups.map(group => (
                  <option key={group.key || group.group_id} value={group.key}>
                    {group.name}{Number(group.member_count) >= 0 ? ` (${group.member_count} members)` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="se-notes">Notes</label>
            <textarea id="se-notes" rows="2" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything the assignee should know..." />
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={submitting}>Cancel</button>
          <button type="button" className="btn-primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Scheduling...' : 'Schedule & Move Stage'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ScheduleEntryModal;
